import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronDown } from 'lucide-react'
import { duration, ease } from '../../lib/motion'

const FAQS = [
  {
    q: 'How does the AI analyse my meeting notes?',
    a: 'It reads the transcript and pulls out anything that looks like an action item, along with who it was assigned to, any deadline mentioned, and how urgent it sounded. You see everything it found before any card is created.',
  },
  {
    q: 'How accurate is it?',
    a: 'It varies with how clearly the notes are written. Well-structured notes produce better results than a rambling transcript. Because you review and edit every task before cards are created, a missed detail costs you a few seconds rather than a wrong card.',
  },
  {
    q: 'What file formats are supported?',
    a: 'Plain text, Markdown and PDF, up to 10MB. You can also paste text directly.',
  },
  {
    q: 'Can I edit tasks before creating Trello cards?',
    a: 'Yes. You can change the title and description, set the assignee, due date and labels, choose which list each card goes to, and deselect anything you do not want.',
  },
  {
    q: 'What data do you store?',
    a: 'Your Trello access token, and a record of the cards you created so they appear in your history. Notes are sent to OpenAI for parsing.',
  },
  {
    q: 'Do I need a paid Trello account?',
    a: 'No. It works with free Trello accounts. You just need to connect the account so cards can be created.',
  },
  {
    q: 'Can I use this for recurring meetings?',
    a: 'Yes. Each set of notes is processed separately, and you choose the board and list each time, so different meetings can go to different places.',
  },
  {
    q: 'How do I disconnect?',
    a: 'From Settings. Disconnecting deletes your stored Trello token and clears the cached team list.',
  },
]

export default function Faq() {
  const [open, setOpen] = useState<number | null>(null)

  return (
    <section className="mx-auto max-w-3xl px-4 py-28">
      <h2 className="text-[1.875rem] font-semibold tracking-[-0.025em] text-white md:text-h2">
        Questions
      </h2>

      <div className="mt-10 divide-y divide-white/[0.06] border-y border-white/[0.06]">
        {FAQS.map((faq, i) => {
          const isOpen = open === i
          const panelId = `faq-panel-${i}`
          const buttonId = `faq-button-${i}`

          return (
            <div key={faq.q}>
              <h3>
                <button
                  id={buttonId}
                  type="button"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className={`flex w-full items-center justify-between gap-4 py-5 text-left text-white/90 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 ${
                    isOpen ? 'text-white' : ''
                  }`}
                >
                  <span className="text-body font-medium">{faq.q}</span>
                  <ChevronDown
                    aria-hidden
                    className={`h-4 w-4 shrink-0 transition-[transform,color] duration-200 ease-out ${
                      isOpen ? 'rotate-180 text-primary-400' : 'text-white/40'
                    }`}
                  />
                </button>
              </h3>

              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: duration.base, ease: ease.standard }}
                    className="overflow-hidden"
                  >
                    <p className="pb-5 text-body text-white/50">{faq.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        })}
      </div>
    </section>
  )
}
